import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import {
  ExclamationCircleIcon,
  TrashIcon,
  ClockIcon,
  ArrowPathIcon,
  ArrowLongRightIcon,
  Square3Stack3DIcon,
  EyeIcon,
} from "@heroicons/react/24/solid";
import Checkbox from "../forms/Checkbox";
import Button from "../layout/Button";
import getButtonStyle from "../../utils/styles";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import ActionButtons from "../layout/ActionButtons";
import { translateText } from "../../utils/translation";
import { getRooms } from "../../slices/rooms";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import Modal from "../layout/Modal";
import { Dialog } from "@headlessui/react";
import TextInput from "../forms/TextInput";
import { useSearchParams } from "react-router-dom";
import Dropdown from "../forms/Dropdown";
import HorizontalRadio from "../forms/HorizontalRadio";
import { daysOfMonth, daysOfWeek } from "../../utils/calendar";
import SelectOrUnselectAll from "../forms/SelectOrUnselectAll";
import DatetimeSelector from "../forms/DatetimeSelector";
import TimeSelector from "../forms/TimeSelector";
import UploadFileDropzone from "../forms/UploadImageDropzone";
import {
  buildDateObjectFromHourAndMinute,
  buildDatetimeObjectFromStringAndNumbers,
  formatDateAsString,
} from "../../utils/datetime";
import Filters from "../layout/Filters";
import Card from "../layout/Card";
import {
  EventState,
  createEvent,
  deleteEvents,
  getEvents,
  updateEvent,
} from "../../slices/content";
import Datepicker from "react-tailwindcss-datepicker";
import getDatepickerLocalisation from "../../utils/localisation";
import DisabledTextInput from "../forms/DisabledTextInput";
import SwitchToggle from "../forms/SwitchToggle";
import DangerZone from "../forms/DangerZone";
import Indicator from "../layout/Indicator";
import TextInputMultiLanguage from "../forms/TextInputMultiLanguage";

export default function Events() {
  const dispatch = useAppDispatch();

  // Get query params
  const [searchParams] = useSearchParams();
  const searchType = searchParams.get("type");
  const dateRangeTerm = searchParams.get("dateRange");

  // Fetch data from server
  useEffect(() => {
    dispatch(getEvents({ type: searchType, dateRange: dateRangeTerm }));
    dispatch(getRooms());
  }, [dateRangeTerm, dispatch, searchType]);

  // Set state
  const [type, setType] = useState({ value: "" });
  const [showQrCode, setShowQrCode] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [selectedEvent, setSelectedEvent] = useState<{
    id: string;
    name: { [key: string]: string };
    target: string;
    type: string;
    choose_rooms_by: string;
    rooms: string[];
    tags: string[];
    schedule: string;
    recurring_schedule_type: string | null;
    recurring_schedule_detail: {
      STD: any;
      STW: Array<{
        id: number;
        value: string;
        start_time: { hour: string; minute: string };
        end_time: { hour: string; minute: string };
      }>;
      STM: Array<{
        id: number;
        value: string;
        start_time: { hour: string; minute: string };
        end_time: { hour: string; minute: string };
      }>;
      SLD: Array<{
        id: number;
        value: string;
        start_time: { hour: string; minute: string };
        end_time: { hour: string; minute: string };
      }>;
    };
    background_image: string | null;
    link: string | null;
    qr_code: string | null;
    status: string;
  }>({
    id: "",
    name: { EN: "", FR: "", IT: "", DE: "" },
    target: "A",
    type: "EVE",
    choose_rooms_by: "name",
    rooms: [],
    tags: [],
    schedule: "O",
    recurring_schedule_type: null,
    recurring_schedule_detail: {
      STD: null,
      STW: [
        {
          id: 0,
          value: "1",
          start_time: { hour: "0", minute: "0" },
          end_time: { hour: "0", minute: "0" },
        },
      ],
      STM: [
        {
          id: 0,
          value: "1",
          start_time: { hour: "0", minute: "0" },
          end_time: { hour: "0", minute: "0" },
        },
      ],
      SLD: [],
    },
    background_image: "",
    link: "",
    qr_code: "",
    status: "A",
  });
  const [createEventForm, setCreateEventForm] = useState<{
    name: { [key: string]: string };
    target: string;
    type: string;
    choose_rooms_by: string;
    rooms: string[];
    tags: string[];
    schedule: string;
    recurring_schedule_type: string;
    recurring_schedule_detail: {
      STD: any;
      STW: Array<{
        id: number;
        value: string;
        start_time: { hour: string; minute: string };
        end_time: { hour: string; minute: string };
      }>;
      STM: Array<{
        id: number;
        value: string;
        start_time: { hour: string; minute: string };
        end_time: { hour: string; minute: string };
      }>;
      SLD: Array<{
        id: number;
        value: string;
        start_time: { hour: string; minute: string };
        end_time: { hour: string; minute: string };
      }>;
    };
    link: string | null;
  }>({
    name: { EN: "", FR: "", IT: "", DE: "" },
    target: "A",
    type: "EVE",
    choose_rooms_by: "name",
    rooms: [],
    tags: [],
    schedule: "O",
    recurring_schedule_type: "STD",
    recurring_schedule_detail: {
      STD: null,
      STW: [
        {
          id: 0,
          value: "1",
          start_time: { hour: "", minute: "" },
          end_time: { hour: "", minute: "" },
        },
      ],
      STM: [
        {
          id: 0,
          value: "1",
          start_time: { hour: "", minute: "" },
          end_time: { hour: "", minute: "" },
        },
      ],
      SLD: [],
    },
    link: "",
  });
  const [recurringSldPlaceholder] = useState({
    startDate: null,
    endDate: null,
  });
  const [oneOffScheduleStartDate, setOneOffScheduleStartDate] = useState<any>({
    startDate: null,
    endDate: null,
  });
  const [oneOffScheduleEndDate, setOneOffScheduleEndDate] = useState<any>({
    startDate: null,
    endDate: null,
  });
  const [oneOffScheduleStartTime, setOneOffScheduleStartTime] = useState<{
    hour: any;
    minute: any;
  }>({ hour: "", minute: "" });
  const [oneOffScheduleEndTime, setOneOffScheduleEndTime] = useState<{
    hour: any;
    minute: any;
  }>({ hour: "", minute: "" });
  const [oneOffScheduleStartEditDate, setOneOffScheduleStartEditDate] =
    useState<any>({
      startDate: null,
      endDate: null,
    });
  const [oneOffScheduleEndEditDate, setOneOffScheduleEndEditDate] =
    useState<any>({
      startDate: null,
      endDate: null,
    });
  const [oneOffScheduleStartEditTime, setOneOffScheduleStartEditTime] =
    useState<{ hour: any; minute: any }>({ hour: "", minute: "" });
  const [oneOffScheduleEndEditTime, setOneOffScheduleEndEditTime] = useState<{
    hour: any;
    minute: any;
  }>({ hour: "", minute: "" });

  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [recurringScheduleStartTime, setRecurringScheduleStartTime] = useState<{
    hour: any;
    minute: any;
  }>({ hour: "", minute: "" });
  const [recurringScheduleEndTime, setRecurringScheduleEndTime] = useState<{
    hour: any;
    minute: any;
  }>({ hour: "", minute: "" });
  const [recurringScheduleStartEditTime, setRecurringScheduleStartEditTime] =
    useState<{ hour: any; minute: any }>({ hour: "", minute: "" });
  const [recurringScheduleEndEditTime, setRecurringScheduleEndEditTime] =
    useState<{ hour: any; minute: any }>({ hour: "", minute: "" });

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const { events, options } = useAppSelector((state) => state.content);
  const { rooms } = useAppSelector((state) => state.rooms);
  const { images } = useAppSelector((state) => state.uploads);
  const language = user === null ? "EN" : user.language;

  // Functions
  function selectEventToEdit(e: any, args: any[]) {
    e.preventDefault();
    const [eventId] = args;
    const eventData = eventsData.filter((event) => event.id === eventId)[0];
    setSelectedEvent({
      id: eventData.id,
      name: eventData.name,
      target: eventData.target[0],
      type: eventData.type[0],
      schedule: eventData.schedule[0],
      recurring_schedule_type:
        eventData.schedule_type !== null ? eventData.schedule_type[0] : "STD",
      recurring_schedule_detail:
        eventData.schedule_detail !== null
          ? {
              STD: eventData.schedule_detail.R.STD,
              STW:
                eventData.schedule_detail.R.STW === null
                  ? [
                      {
                        id: 0,
                        value: "1",
                        start_time: { hour: "", minute: "" },
                        end_time: { hour: "", minute: "" },
                      },
                    ]
                  : eventData.schedule_detail.R.STW.map((x: any) => ({
                      ...x,
                      value: x.day_of_week,
                    })),
              STM:
                eventData.schedule_detail.R.STM === null
                  ? [
                      {
                        id: 0,
                        value: "1",
                        start_time: { hour: "", minute: "" },
                        end_time: { hour: "", minute: "" },
                      },
                    ]
                  : eventData.schedule_detail.R.STM.map((x: any) => ({
                      ...x,
                      value: x.day_of_month,
                    })),
              SLD:
                eventData.schedule_detail.R.SLD === null ||
                eventData.schedule_detail.R.SLD === undefined
                  ? []
                  : eventData.schedule_detail.R.SLD.map((x: any) => ({
                      ...x,
                      value: x.date,
                    })),
            }
          : {
              STD: null,
              STW: [
                {
                  id: 0,
                  value: "1",
                  start_time: { hour: "0", minute: "0" },
                  end_time: { hour: "0", minute: "0" },
                },
              ],
              STM: [
                {
                  id: 0,
                  value: "1",
                  start_time: { hour: "0", minute: "0" },
                  end_time: { hour: "0", minute: "0" },
                },
              ],
              SLD: [],
            },
      status: eventData.status[0],
      choose_rooms_by: "name",
      rooms: eventData.rooms,
      tags: [],
      background_image: eventData.background_image,
      link: eventData.link,
      qr_code: eventData.qr_code,
    });
    if (eventData.schedule[0] === "O") {
      setOneOffScheduleStartEditDate({
        startDate: eventData.schedule_detail.O.start_date,
        endDate: eventData.schedule_detail.O.start_date,
      });
      setOneOffScheduleEndEditDate({
        startDate: eventData.schedule_detail.O.end_date,
        endDate: eventData.schedule_detail.O.end_date,
      });
      setOneOffScheduleStartEditTime({
        hour: eventData.schedule_detail.O.start_time.hour.toString(),
        minute: eventData.schedule_detail.O.start_time.minute.toString(),
      });
      setOneOffScheduleEndEditTime({
        hour: eventData.schedule_detail.O.end_time.hour.toString(),
        minute: eventData.schedule_detail.O.end_time.minute.toString(),
      });
    } else if (eventData.schedule[0] === "R") {
      if (
        eventData.schedule_type !== null &&
        eventData.schedule_type[0] === "STD"
      ) {
        setRecurringScheduleStartEditTime({
          hour: eventData.schedule_detail.R.STD.start_time.hour.toString(),
          minute: eventData.schedule_detail.R.STD.start_time.minute.toString(),
        });
        setRecurringScheduleEndEditTime({
          hour: eventData.schedule_detail.R.STD.end_time.hour.toString(),
          minute: eventData.schedule_detail.R.STD.end_time.minute.toString(),
        });
      }
    }
    setShowEditModal(true);
  }

  function openCreateEventModal(e: any) {
    e.preventDefault();
    setShowCreateModal(true);
  }

  function selectOrUnselectAllCreateRooms(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableRooms =
        rooms.data === null ? [] : rooms.data.map((x) => x.id);
      setCreateEventForm({ ...createEventForm, rooms: allAvailableRooms });
    } else {
      setCreateEventForm({ ...createEventForm, rooms: [] });
    }
  }

  function selectOrUnselectAllCreateTags(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableTags = roomTags.map((x: any) => x.id);
      setCreateEventForm({ ...createEventForm, tags: allAvailableTags });
    } else {
      setCreateEventForm({ ...createEventForm, tags: [] });
    }
  }

  function updateRoomForCreateEvent(roomId: string) {
    if (createEventForm.rooms.includes(roomId)) {
      const updatedRooms = createEventForm.rooms.filter((x) => x !== roomId);
      setCreateEventForm({ ...createEventForm, rooms: updatedRooms });
    } else {
      const updatedRooms = [...createEventForm.rooms, roomId];
      setCreateEventForm({ ...createEventForm, rooms: updatedRooms });
    }
  }

  function updateTagForCreateEvent(tag: string) {
    const existingTags = createEventForm.tags;
    if (existingTags.includes(tag)) {
      const updatedTags = existingTags.filter((x) => x !== tag);
      setCreateEventForm({ ...createEventForm, tags: updatedTags });
    } else {
      const updatedTags = [...existingTags, tag];
      setCreateEventForm({ ...createEventForm, tags: updatedTags });
    }
  }

  function selectOrUnselectAllEditRooms(e: any, args: any[]) {
    e.preventDefault();
    if (selectedEvent === null) return;
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableRooms =
        rooms.data === null ? [] : rooms.data.map((x) => x.id);
      setSelectedEvent({ ...selectedEvent, rooms: allAvailableRooms });
    } else {
      setSelectedEvent({ ...selectedEvent, rooms: [] });
    }
  }

  function selectOrUnselectAllEditTags(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableTags = roomTags.map((x: any) => x.id);
      setSelectedEvent({ ...selectedEvent, tags: allAvailableTags });
    } else {
      setSelectedEvent({ ...selectedEvent, tags: [] });
    }
  }

  function updateRoomForEditEvent(roomId: string) {
    if (selectedEvent === null) return;
    if (selectedEvent.rooms.includes(roomId)) {
      const updatedRooms = selectedEvent.rooms.filter((x) => x !== roomId);
      setSelectedEvent({ ...selectedEvent, rooms: updatedRooms });
    } else {
      const updatedRooms = [...createEventForm.rooms, roomId];
      setSelectedEvent({ ...selectedEvent, rooms: updatedRooms });
    }
  }

  function updateTagForEditEvent(tag: string) {
    if (selectedEvent === null) return;
    const selectedEventTags = selectedEvent.tags;
    if (selectedEventTags.includes(tag)) {
      const updatedTags = selectedEventTags.filter((x) => x !== tag);
      setSelectedEvent({ ...selectedEvent, tags: updatedTags });
    } else {
      const updatedTags = [...selectedEventTags, tag];
      setSelectedEvent({ ...selectedEvent, tags: updatedTags });
    }
  }

  async function confirmCreateEvent(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    const {
      name,
      target,
      type,
      choose_rooms_by: chooseRoomsBy,
      rooms,
      tags,
      schedule,
      recurring_schedule_type: recurringScheduleType,
      recurring_schedule_detail: recurringScheduleDetail,
      link,
    } = createEventForm;

    // Validate text fields
    if (name[language] === "") {
      dispatch(
        addAlert(generateAlertText("missing_name", language), "danger", 5000),
      );
      return;
    }

    // Validate rooms
    let chosenRooms: string[] = [];
    if (target === "S") {
      if (chooseRoomsBy === "name") {
        chosenRooms = rooms;
      } else {
        chosenRooms = roomsData
          .filter((room) => tags.includes(room.tag[0]))
          .map((room) => room.id);
      }
      if (chosenRooms.length === 0) {
        dispatch(
          addAlert(
            generateAlertText("missing_rooms", language),
            "danger",
            5000,
          ),
        );
        return;
      }
    }

    // Validate schedule
    let scheduleObj: any = {
      O: null,
      R: { STD: null, STW: null, STM: null, SLD: null },
    };
    if (schedule === "O") {
      const { startDate } = oneOffScheduleStartDate;
      const { endDate } = oneOffScheduleEndDate;
      const { hour: startHour, minute: startMinute } = oneOffScheduleStartTime;
      const { hour: endHour, minute: endMinute } = oneOffScheduleEndTime;
      if (startDate === null || startHour === "" || startMinute === "") {
        dispatch(
          addAlert(
            generateAlertText("missing_start_datetime", language),
            "danger",
            5000,
          ),
        );
        return;
      }
      if (endDate === null || endHour === "" || endMinute === "") {
        dispatch(
          addAlert(
            generateAlertText("missing_end_datetime", language),
            "danger",
            5000,
          ),
        );
        return;
      }
      const startDatetime = buildDatetimeObjectFromStringAndNumbers(
        startDate,
        startHour,
        startMinute,
      );
      const endDatetime = buildDatetimeObjectFromStringAndNumbers(
        endDate,
        endHour,
        endMinute,
      );
      if (startDatetime >= endDatetime) {
        dispatch(
          addAlert(
            generateAlertText("start_after_end", language),
            "danger",
            3000,
          ),
        );
        return;
      }
      scheduleObj.O = {
        start_date: formatDateAsString(startDatetime),
        end_date: formatDateAsString(endDatetime),
        start_time: {
          hour: parseInt(startHour, 10),
          minute: parseInt(startMinute, 10),
        },
        end_time: {
          hour: parseInt(endHour, 10),
          minute: parseInt(endMinute, 10),
        },
      };
    } else if (schedule === "R") {
      if (recurringScheduleType === "STD") {
        const { hour: startHour, minute: startMinute } =
          recurringScheduleStartTime;
        const { hour: endHour, minute: endMinute } = recurringScheduleEndTime;
        if (startHour === "" || startMinute === "") {
          dispatch(
            addAlert(
              generateAlertText("missing_start_time", language),
              "danger",
              5000,
            ),
          );
          return;
        }
        const startTime = buildDateObjectFromHourAndMinute(
          startHour,
          startMinute,
        );
        const endTime = buildDateObjectFromHourAndMinute(endHour, endMinute);
        if (startTime >= endTime) {
          dispatch(
            addAlert(
              generateAlertText("start_after_end", language),
              "danger",
              3000,
            ),
          );
          return;
        }
        scheduleObj.R.STD = {
          start_time: {
            hour: parseInt(startHour, 10),
            minute: parseInt(startMinute, 10),
          },
          end_time: {
            hour: parseInt(endHour, 10),
            minute: parseInt(endMinute, 10),
          },
        };
      } else if (recurringScheduleType === "STW") {
        const recurringScheduleDetailValues = recurringScheduleDetail.STW;
        for (let i = 0; i < recurringScheduleDetailValues.length; i++) {
          const {
            value,
            start_time: startTime,
            end_time: endTime,
          } = recurringScheduleDetailValues[i];
          if (
            value === "" ||
            startTime.hour === "" ||
            startTime.minute === "" ||
            endTime.hour === "" ||
            endTime.minute === ""
          ) {
            dispatch(
              addAlert(
                generateAlertText(
                  "missing_recurring_schedule_detail",
                  language,
                ),
                "danger",
                5000,
              ),
            );
            return;
          }
          const formattedStartHour = parseInt(startTime.hour, 10);
          const formattedEndHour = parseInt(endTime.hour, 10);
          const formattedStartMinute = parseInt(startTime.minute, 10);
          const formattedEndMinute = parseInt(endTime.minute, 10);
          const startTimeObj = buildDateObjectFromHourAndMinute(
            formattedStartHour,
            formattedStartMinute,
          );
          const endTimeObj = buildDateObjectFromHourAndMinute(
            formattedEndHour,
            formattedEndMinute,
          );
          if (startTimeObj >= endTimeObj) {
            dispatch(
              addAlert(
                generateAlertText("start_after_end", language),
                "danger",
                3000,
              ),
            );
            return;
          }
        }
        scheduleObj.R.STW = recurringScheduleDetailValues.map(
          (x: any, idx: number) => ({
            id: idx,
            day_of_week: x.value,
            start_time: {
              hour: parseInt(x.start_time.hour, 10),
              minute: parseInt(x.start_time.minute, 10),
            },
            end_time: {
              hour: parseInt(x.end_time.hour, 10),
              minute: parseInt(x.end_time.minute, 10),
            },
          }),
        );
      } else if (recurringScheduleType === "STM") {
        const recurringScheduleDetailValues = recurringScheduleDetail.STM;
        for (let i = 0; i < recurringScheduleDetailValues.length; i++) {
          const {
            value,
            start_time: startTime,
            end_time: endTime,
          } = recurringScheduleDetailValues[i];
          if (
            value === "" ||
            startTime.hour === "" ||
            startTime.minute === "" ||
            endTime.hour === "" ||
            endTime.minute === ""
          ) {
            dispatch(
              addAlert(
                generateAlertText(
                  "missing_recurring_schedule_detail",
                  language,
                ),
                "danger",
                5000,
              ),
            );
            return;
          }
          const formattedStartHour = parseInt(startTime.hour, 10);
          const formattedEndHour = parseInt(endTime.hour, 10);
          const formattedStartMinute = parseInt(startTime.minute, 10);
          const formattedEndMinute = parseInt(endTime.minute, 10);
          const startTimeObj = buildDateObjectFromHourAndMinute(
            formattedStartHour,
            formattedStartMinute,
          );
          const endTimeObj = buildDateObjectFromHourAndMinute(
            formattedEndHour,
            formattedEndMinute,
          );
          if (startTimeObj >= endTimeObj) {
            dispatch(
              addAlert(
                generateAlertText("start_after_end", language),
                "danger",
                3000,
              ),
            );
            return;
          }
        }
        scheduleObj.R.STM = recurringScheduleDetailValues.map(
          (x: any, idx: number) => ({
            id: idx,
            day_of_month: x.value,
            start_time: {
              hour: parseInt(x.start_time.hour, 10),
              minute: parseInt(x.start_time.minute, 10),
            },
            end_time: {
              hour: parseInt(x.end_time.hour, 10),
              minute: parseInt(x.end_time.minute, 10),
            },
          }),
        );
      } else if (recurringScheduleType === "SLD") {
        const recurringScheduleDetailValues = recurringScheduleDetail.SLD;
        if (recurringScheduleDetailValues.length === 0) {
          dispatch(
            addAlert(
              generateAlertText("missing_selected_days", language),
              "danger",
              5000,
            ),
          );
          return;
        }
        for (let i = 0; i < recurringScheduleDetailValues.length; i++) {
          const {
            value,
            start_time: startTime,
            end_time: endTime,
          } = recurringScheduleDetailValues[i];
          if (
            value === "" ||
            startTime.hour === "" ||
            startTime.minute === "" ||
            endTime.hour === "" ||
            endTime.minute === ""
          ) {
            dispatch(
              addAlert(
                generateAlertText(
                  "missing_recurring_schedule_detail",
                  language,
                ),
                "danger",
                5000,
              ),
            );
            return;
          }
          const formattedStartHour = parseInt(startTime.hour, 10);
          const formattedEndHour = parseInt(endTime.hour, 10);
          const formattedStartMinute = parseInt(startTime.minute, 10);
          const formattedEndMinute = parseInt(endTime.minute, 10);
          const startTimeObj = buildDateObjectFromHourAndMinute(
            formattedStartHour,
            formattedStartMinute,
          );
          const endTimeObj = buildDateObjectFromHourAndMinute(
            formattedEndHour,
            formattedEndMinute,
          );
          if (startTimeObj >= endTimeObj) {
            dispatch(
              addAlert(
                generateAlertText("start_after_end", language),
                "danger",
                3000,
              ),
            );
            return;
          }
        }
        scheduleObj.R.SLD = recurringScheduleDetailValues.map(
          (x: any, idx: number) => ({
            id: idx,
            date: x.value,
            start_time: {
              hour: parseInt(x.start_time.hour, 10),
              minute: parseInt(x.start_time.minute, 10),
            },
            end_time: {
              hour: parseInt(x.end_time.hour, 10),
              minute: parseInt(x.end_time.minute, 10),
            },
          }),
        );
      }
    }

    // Add item image if uploaded
    const processedPhotoKeys = Object.keys(images.events.data);
    const processedPhotos = [];
    for (let i = 0; i < processedPhotoKeys.length; i += 1) {
      const key = processedPhotoKeys[i];
      const candidate = images.events.data[key];
      if (candidate.data === null || !candidate.success) {
        continue;
      }
      processedPhotos.push({
        id: key,
        source: "processed",
        data: candidate.data,
      });
    }

    setShowCreateModal(false);
    const res: any = await dispatch(
      createEvent({
        name,
        type,
        property: user.property.id,
        schedule,
        scheduleDetail: scheduleObj,
        target,
        rooms: chosenRooms,
        backgroundImage: processedPhotos.length > 0 ? processedPhotos[0] : null,
        link: link === "" ? null : link,
        status: "A",
      }),
    );
    if (res.payload.status === 201) {
      dispatch(
        addAlert(generateAlertText("create_event", language), "success", 3000),
      );
      setCreateEventForm({
        name: { EN: "", FR: "", IT: "", DE: "" },
        target: "A",
        type: "EVE",
        choose_rooms_by: "name",
        rooms: [],
        tags: [],
        schedule: "O",
        recurring_schedule_type: "STD",
        recurring_schedule_detail: {
          STD: null,
          STW: [
            {
              id: 0,
              value: "1",
              start_time: { hour: "0", minute: "0" },
              end_time: { hour: "0", minute: "0" },
            },
          ],
          STM: [
            {
              id: 0,
              value: "1",
              start_time: { hour: "0", minute: "0" },
              end_time: { hour: "0", minute: "0" },
            },
          ],
          SLD: [],
        },
        link: "",
      });
    } else {
      dispatch(
        addAlert(generateAlertText("create_error", language), "danger", 3000),
      );
      setShowCreateModal(true);
    }
  }

  async function confirmEditEvent(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    const {
      id,
      name,
      target,
      type,
      choose_rooms_by: chooseRoomsBy,
      rooms,
      tags,
      schedule,
      recurring_schedule_type: recurringScheduleType,
      recurring_schedule_detail: recurringScheduleDetail,
      background_image: backgroundImage,
      link,
      status,
    } = selectedEvent;

    // Validate text fields
    if (name[language] === "") {
      dispatch(
        addAlert(generateAlertText("missing_name", language), "danger", 5000),
      );
      return;
    }

    // Validate rooms
    let chosenRooms: string[] = [];
    if (target === "S") {
      if (chooseRoomsBy === "name") {
        chosenRooms = rooms;
      } else {
        chosenRooms = roomsData
          .filter((room) => tags.includes(room.tag[0]))
          .map((room) => room.id);
      }
      if (chosenRooms.length === 0) {
        dispatch(
          addAlert(
            generateAlertText("missing_rooms", language),
            "danger",
            5000,
          ),
        );
        return;
      }
    }

    // Validate schedule
    let scheduleObj: any = {
      O: null,
      R: { STD: null, STW: null, STM: null, SLD: null },
    };
    if (schedule === "O") {
      const { startDate } = oneOffScheduleStartEditDate;
      const { endDate } = oneOffScheduleEndEditDate;
      const { hour: startHour, minute: startMinute } =
        oneOffScheduleStartEditTime;
      const { hour: endHour, minute: endMinute } = oneOffScheduleEndEditTime;
      if (startDate === null || startHour === "" || startMinute === "") {
        dispatch(
          addAlert(
            generateAlertText("missing_start_datetime", language),
            "danger",
            5000,
          ),
        );
        return;
      }
      if (endDate === null || endHour === "" || endMinute === "") {
        dispatch(
          addAlert(
            generateAlertText("missing_end_datetime", language),
            "danger",
            5000,
          ),
        );
        return;
      }
      const startDatetime = buildDatetimeObjectFromStringAndNumbers(
        startDate,
        startHour,
        startMinute,
      );
      const endDatetime = buildDatetimeObjectFromStringAndNumbers(
        endDate,
        endHour,
        endMinute,
      );
      if (startDatetime >= endDatetime) {
        dispatch(
          addAlert(
            generateAlertText("start_after_end", language),
            "danger",
            3000,
          ),
        );
        return;
      }
      scheduleObj.O = {
        start_date: formatDateAsString(startDatetime),
        end_date: formatDateAsString(endDatetime),
        start_time: {
          hour: parseInt(startHour, 10),
          minute: parseInt(startMinute, 10),
        },
        end_time: {
          hour: parseInt(endHour, 10),
          minute: parseInt(endMinute, 10),
        },
      };
    } else if (schedule === "R") {
      if (recurringScheduleType === "STD") {
        const { hour: startHour, minute: startMinute } =
          recurringScheduleStartEditTime;
        const { hour: endHour, minute: endMinute } =
          recurringScheduleEndEditTime;
        if (startHour === "" || startMinute === "") {
          dispatch(
            addAlert(
              generateAlertText("missing_start_time", language),
              "danger",
              5000,
            ),
          );
          return;
        }
        const startTime = buildDateObjectFromHourAndMinute(
          startHour,
          startMinute,
        );
        const endTime = buildDateObjectFromHourAndMinute(endHour, endMinute);
        if (startTime >= endTime) {
          dispatch(
            addAlert(
              generateAlertText("start_after_end", language),
              "danger",
              3000,
            ),
          );
          return;
        }
        scheduleObj.R.STD = {
          start_time: {
            hour: parseInt(startHour, 10),
            minute: parseInt(startMinute, 10),
          },
          end_time: {
            hour: parseInt(endHour, 10),
            minute: parseInt(endMinute, 10),
          },
        };
      } else if (recurringScheduleType === "STW") {
        const recurringScheduleDetailValues = recurringScheduleDetail.STW;
        for (let i = 0; i < recurringScheduleDetailValues.length; i++) {
          const {
            value,
            start_time: startTime,
            end_time: endTime,
          } = recurringScheduleDetailValues[i];
          if (
            value === "" ||
            startTime.hour === "" ||
            startTime.minute === "" ||
            endTime.hour === "" ||
            endTime.minute === ""
          ) {
            dispatch(
              addAlert(
                generateAlertText(
                  "missing_recurring_schedule_detail",
                  language,
                ),
                "danger",
                5000,
              ),
            );
            return;
          }
          const formattedStartHour = parseInt(startTime.hour, 10);
          const formattedEndHour = parseInt(endTime.hour, 10);
          const formattedStartMinute = parseInt(startTime.minute, 10);
          const formattedEndMinute = parseInt(endTime.minute, 10);
          const startTimeObj = buildDateObjectFromHourAndMinute(
            formattedStartHour,
            formattedStartMinute,
          );
          const endTimeObj = buildDateObjectFromHourAndMinute(
            formattedEndHour,
            formattedEndMinute,
          );
          if (startTimeObj >= endTimeObj) {
            dispatch(
              addAlert(
                generateAlertText("start_after_end", language),
                "danger",
                3000,
              ),
            );
            return;
          }
        }
        scheduleObj.R.STW = recurringScheduleDetailValues.map(
          (x: any, idx: number) => ({
            id: idx,
            day_of_week: x.value,
            start_time: {
              hour: parseInt(x.start_time.hour, 10),
              minute: parseInt(x.start_time.minute, 10),
            },
            end_time: {
              hour: parseInt(x.end_time.hour, 10),
              minute: parseInt(x.end_time.minute, 10),
            },
          }),
        );
      } else if (recurringScheduleType === "STM") {
        const recurringScheduleDetailValues = recurringScheduleDetail.STM;
        for (let i = 0; i < recurringScheduleDetailValues.length; i++) {
          const {
            value,
            start_time: startTime,
            end_time: endTime,
          } = recurringScheduleDetailValues[i];
          if (
            value === "" ||
            startTime.hour === "" ||
            startTime.minute === "" ||
            endTime.hour === "" ||
            endTime.minute === ""
          ) {
            dispatch(
              addAlert(
                generateAlertText(
                  "missing_recurring_schedule_detail",
                  language,
                ),
                "danger",
                5000,
              ),
            );
            return;
          }
          const formattedStartHour = parseInt(startTime.hour, 10);
          const formattedEndHour = parseInt(endTime.hour, 10);
          const formattedStartMinute = parseInt(startTime.minute, 10);
          const formattedEndMinute = parseInt(endTime.minute, 10);
          const startTimeObj = buildDateObjectFromHourAndMinute(
            formattedStartHour,
            formattedStartMinute,
          );
          const endTimeObj = buildDateObjectFromHourAndMinute(
            formattedEndHour,
            formattedEndMinute,
          );
          if (startTimeObj >= endTimeObj) {
            dispatch(
              addAlert(
                generateAlertText("start_after_end", language),
                "danger",
                3000,
              ),
            );
            return;
          }
        }
        scheduleObj.R.STM = recurringScheduleDetailValues.map(
          (x: any, idx: number) => ({
            id: idx,
            day_of_month: x.value,
            start_time: {
              hour: parseInt(x.start_time.hour, 10),
              minute: parseInt(x.start_time.minute, 10),
            },
            end_time: {
              hour: parseInt(x.end_time.hour, 10),
              minute: parseInt(x.end_time.minute, 10),
            },
          }),
        );
      } else if (recurringScheduleType === "SLD") {
        const recurringScheduleDetailValues = recurringScheduleDetail.SLD;
        if (recurringScheduleDetailValues.length === 0) {
          dispatch(
            addAlert(
              generateAlertText("missing_selected_days", language),
              "danger",
              5000,
            ),
          );
          return;
        }
        for (let i = 0; i < recurringScheduleDetailValues.length; i++) {
          const {
            value,
            start_time: startTime,
            end_time: endTime,
          } = recurringScheduleDetailValues[i];
          if (
            value === "" ||
            startTime.hour === "" ||
            startTime.minute === "" ||
            endTime.hour === "" ||
            endTime.minute === ""
          ) {
            dispatch(
              addAlert(
                generateAlertText(
                  "missing_recurring_schedule_detail",
                  language,
                ),
                "danger",
                5000,
              ),
            );
            return;
          }
          const formattedStartHour = parseInt(startTime.hour, 10);
          const formattedEndHour = parseInt(endTime.hour, 10);
          const formattedStartMinute = parseInt(startTime.minute, 10);
          const formattedEndMinute = parseInt(endTime.minute, 10);
          const startTimeObj = buildDateObjectFromHourAndMinute(
            formattedStartHour,
            formattedStartMinute,
          );
          const endTimeObj = buildDateObjectFromHourAndMinute(
            formattedEndHour,
            formattedEndMinute,
          );
          if (startTimeObj >= endTimeObj) {
            dispatch(
              addAlert(
                generateAlertText("start_after_end", language),
                "danger",
                3000,
              ),
            );
            return;
          }
        }
        scheduleObj.R.SLD = recurringScheduleDetailValues.map(
          (x: any, idx: number) => ({
            id: idx,
            date: x.value,
            start_time: {
              hour: parseInt(x.start_time.hour, 10),
              minute: parseInt(x.start_time.minute, 10),
            },
            end_time: {
              hour: parseInt(x.end_time.hour, 10),
              minute: parseInt(x.end_time.minute, 10),
            },
          }),
        );
      }
    }

    // Confirm and validate image
    const savedImage: any = backgroundImage;

    // Build object for processed photos
    const processedPhotoKeys = Object.keys(images.events.data);
    const processedPhotos: any = [];
    for (let i = 0; i < processedPhotoKeys.length; i += 1) {
      const key = processedPhotoKeys[i];
      const candidate = images.events.data[key];
      if (candidate.data === null || !candidate.success) {
        continue;
      }
      processedPhotos.push({
        id: key,
        source: "processed",
        data: candidate.data,
      });
    }
    let editImage: any = null;
    if (!images.events.removed.includes(savedImage)) {
      editImage = { id: 0, source: "saved", data: savedImage };
    } else {
      editImage = processedPhotos.length > 0 ? processedPhotos[0] : null;
    }

    setShowEditModal(false);
    const res: any = await dispatch(
      updateEvent({
        eventId: id,
        name,
        type,
        property: user.property.id,
        schedule,
        scheduleDetail: scheduleObj,
        target,
        rooms: chosenRooms,
        backgroundImage: editImage,
        link: link === "" ? null : link,
        status,
      }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(generateAlertText("update_event", language), "success", 3000),
      );
      setSelectedEvent({
        id: "",
        name: { EN: "", FR: "", IT: "", DE: "" },
        target: "A",
        type: "EVE",
        choose_rooms_by: "name",
        rooms: [],
        tags: [],
        schedule: "O",
        recurring_schedule_type: null,
        recurring_schedule_detail: {
          STD: null,
          STW: [
            {
              id: 0,
              value: "1",
              start_time: { hour: "0", minute: "0" },
              end_time: { hour: "0", minute: "0" },
            },
          ],
          STM: [
            {
              id: 0,
              value: "1",
              start_time: { hour: "0", minute: "0" },
              end_time: { hour: "0", minute: "0" },
            },
          ],
          SLD: [],
        },
        background_image: null,
        link: null,
        qr_code: null,
        status: "A",
      });
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  async function confirmDeleteEvent(e: any, args: any[]) {
    e.preventDefault();
    const [eventIds] = args;
    if (eventIds.length === 0) return;
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 3000),
      );
      return;
    }
    if (eventIds === null || eventIds === undefined) {
      dispatch(
        addAlert(generateAlertText("missing_events", language), "danger", 3000),
      );
      return;
    }
    setShowEditModal(false);
    const res: any = await dispatch(deleteEvents({ eventIds: [eventIds] }));
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("delete_success", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("delete_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  function increaseRecurringDetailValueCount(e: any, args: any[]) {
    e.preventDefault();
    const [type] = args;
    if (type === "STW") {
      const existingValues = [...createEventForm.recurring_schedule_detail.STW];
      const newId = existingValues.length;
      const newValues = [
        ...existingValues,
        {
          id: newId,
          value: "1",
          start_time: { hour: "0", minute: "0" },
          end_time: { hour: "0", minute: "0" },
        },
      ];
      setCreateEventForm({
        ...createEventForm,
        recurring_schedule_detail: {
          ...createEventForm.recurring_schedule_detail,
          STW: newValues,
        },
      });
    } else if (type === "STM") {
      const existingValues = [...createEventForm.recurring_schedule_detail.STM];
      const newId = existingValues.length;
      existingValues.push({
        id: newId,
        value: "1",
        start_time: { hour: "0", minute: "0" },
        end_time: { hour: "0", minute: "0" },
      });
      setCreateEventForm({
        ...createEventForm,
        recurring_schedule_detail: {
          ...createEventForm.recurring_schedule_detail,
          STM: existingValues,
        },
      });
    }
  }

  function addDateToRecurringSld(value: any) {
    const existingValues = [...createEventForm.recurring_schedule_detail.SLD];
    const newId = existingValues.length;
    const newValues = [
      ...existingValues,
      {
        id: newId,
        value: value.startDate,
        start_time: { hour: "0", minute: "0" },
        end_time: { hour: "0", minute: "0" },
      },
    ];
    setCreateEventForm({
      ...createEventForm,
      recurring_schedule_detail: {
        ...createEventForm.recurring_schedule_detail,
        SLD: newValues,
      },
    });
  }

  function increaseRecurringDetailValueEditCount(e: any, args: any[]) {
    e.preventDefault();
    const [type] = args;
    if (type === "STW") {
      const existingValues = [...selectedEvent.recurring_schedule_detail.STW];
      const newId = existingValues.length;
      const newValues = [
        ...existingValues,
        {
          id: newId,
          value: "1",
          start_time: { hour: "0", minute: "0" },
          end_time: { hour: "0", minute: "0" },
        },
      ];
      setSelectedEvent({
        ...selectedEvent,
        recurring_schedule_detail: {
          ...selectedEvent.recurring_schedule_detail,
          STW: newValues,
        },
      });
    } else if (type === "STM") {
      const existingValues = [...selectedEvent.recurring_schedule_detail.STM];
      const newId = existingValues.length;
      existingValues.push({
        id: newId,
        value: "1",
        start_time: { hour: "0", minute: "0" },
        end_time: { hour: "0", minute: "0" },
      });
      setSelectedEvent({
        ...selectedEvent,
        recurring_schedule_detail: {
          ...selectedEvent.recurring_schedule_detail,
          STM: existingValues,
        },
      });
    }
  }

  function addDateToRecurringEditSld(value: any) {
    const existingValues = [...selectedEvent.recurring_schedule_detail.SLD];
    const newId = existingValues.length;
    const newValues = [
      ...existingValues,
      {
        id: newId,
        value: value.startDate,
        start_time: { hour: "0", minute: "0" },
        end_time: { hour: "0", minute: "0" },
      },
    ];
    setSelectedEvent({
      ...selectedEvent,
      recurring_schedule_detail: {
        ...selectedEvent.recurring_schedule_detail,
        SLD: newValues,
      },
    });
  }

  function getSavedImages() {
    if (selectedEvent === null) return [];
    if (selectedEvent.background_image === null) return [];
    if (images.events.removed.includes(selectedEvent.background_image))
      return [];
    return [
      {
        id: selectedEvent.background_image,
        data: selectedEvent.background_image,
      },
    ];
  }

  // Format rooms data and filter by query param tag
  const roomsData = rooms.data === null ? [] : rooms.data;
  const eventsData = events.data === null ? [] : events.data;
  const eventTypes =
    options.events === undefined
      ? []
      : Object.keys(options.events).map((key) => ({
          value: key,
          label: options.events[key][language],
        }));
  const eventTypeChoices =
    options.events === undefined
      ? []
      : Object.keys(options.events).map((key) => [
          key,
          options.events[key][language],
        ]);
  const eventScheduleChoices =
    options.schedule === undefined
      ? []
      : Object.keys(options.schedule).map((key) => [
          key,
          options.schedule[key][language],
        ]);
  const chooseRoomsBy = [
    ["name", translateText("name", language)],
    ["tag", translateText("tag_single", language)],
  ];
  const targetTypes = [
    ["A", translateText("all", language)],
    ["S", translateText("specific", language)],
  ];
  const eventScheduleRecurringChoices =
    options.recurring === undefined
      ? []
      : Object.keys(options.recurring).map((key) => [
          key,
          options.recurring[key][language],
        ]);
  const daysOfTheWeek = daysOfWeek(language);
  const daysOfTheMonth = daysOfMonth();
  const hours = Array.from({ length: 24 }, (x, i) => i);
  const minutes = Array.from({ length: 60 }, (x, i) => i);

  // Create tags object for filters
  let roomTags: any = [];
  for (let i = 0; i < roomsData.length; i++) {
    const candidateTags = roomsData[i].tag;
    for (let j = 0; j < candidateTags.length; j++) {
      if (!roomTags.includes(candidateTags[j])) {
        roomTags.push(candidateTags[j]);
      }
    }
  }
  roomTags.sort();
  roomTags = roomTags.map((x: any) => ({ id: x, label: x }));

  return (
    <Template>
      {/* Header */}
      <Header
        text={translateText("events_title", language)}
        actions={
          <ActionButtons
            primaryButton={
              <Button
                type="button"
                className={`${getButtonStyle("solid", "info")}`}
                onClick={openCreateEventModal}
              >
                <PlusCircleIcon
                  className={`h-5 w-5 text-white mr-1`}
                  aria-hidden="true"
                />
                {translateText("create_event", language)}
              </Button>
            }
          />
        }
      />

      {/* Create event modal */}
      <Modal
        show={showCreateModal}
        setShow={setShowCreateModal}
        confirmText={translateText("create_event", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmCreateEvent}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("create_event", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Name */}
              <TextInputMultiLanguage
                id="name"
                type="text"
                label={translateText("name", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={createEventForm}
                stateName="name"
                onChange={setCreateEventForm}
                defaultLanguage={language}
                languageChoices={
                  eventsData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(eventsData[0].name)
                }
              />

              {/* Event type */}
              <Dropdown
                id="type"
                className="col-span-full"
                label={translateText("type", language)}
                showLabel={true}
                required={true}
                state={createEventForm}
                stateName="type"
                onChange={setCreateEventForm}
                defaultValue="EVE"
              >
                {eventTypeChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Target */}
              <HorizontalRadio
                label={translateText("target_rooms", language)}
                small={translateText("target_rooms_subtext", language)}
              >
                <>
                  {targetTypes.map((choice) => (
                    <div key={choice[0]} className="flex items-center">
                      <input
                        id={choice[0]}
                        name="build_type"
                        type="radio"
                        defaultChecked={createEventForm.target === choice[0]}
                        className="h-4 w-4 border-gray-300 text-primary focus:ring-primary rounded"
                        value={choice[0]}
                        onChange={(e) =>
                          setCreateEventForm({
                            ...createEventForm,
                            target: e.target.value,
                          })
                        }
                      />
                      <label
                        htmlFor={choice[0]}
                        className="ml-3 block text-sm font-medium leading-6 text-black"
                      >
                        {choice[1]}
                      </label>
                    </div>
                  ))}
                </>
              </HorizontalRadio>

              {createEventForm.target === "S" && (
                <>
                  {/* Choose rooms by */}
                  <Dropdown
                    id="choose_rooms_by"
                    className="col-span-full"
                    label={translateText("choose_rooms_by", language)}
                    small={translateText("choose_rooms_by_subtext", language)}
                    showLabel={true}
                    required={true}
                    state={createEventForm}
                    stateName="choose_rooms_by"
                    onChange={setCreateEventForm}
                    defaultValue="name"
                  >
                    {chooseRoomsBy.map((opt) => (
                      <option key={opt[0]} value={opt[0]}>
                        {opt[1]}
                      </option>
                    ))}
                  </Dropdown>

                  {/* Rooms */}
                  {createEventForm.choose_rooms_by === "name" ? (
                    <>
                      <div>
                        <label className="mb-1 block text-left text-sm font-medium text-black">
                          {translateText("rooms", language)}
                        </label>
                        <p className="text-left text-xs italic text-secondary">
                          {translateText("rooms_template_subtext", language)}
                        </p>
                      </div>

                      {/* Select/unselect all buttons */}
                      <SelectOrUnselectAll
                        handler={selectOrUnselectAllCreateRooms}
                        language={language}
                      />
                      <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                        {roomsData.map((room: any) => (
                          <Checkbox
                            key={room.id}
                            id={room.id}
                            onChange={updateRoomForCreateEvent}
                            checked={createEventForm.rooms.includes(room.id)}
                            label={room.name}
                          />
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="mb-1 block text-left text-sm font-medium text-black">
                          {translateText("tag", language)}
                        </label>
                        <p className="text-left text-xs italic text-secondary">
                          {translateText(
                            "select_tags_for_rooms_campaign",
                            language,
                          )}
                        </p>
                      </div>

                      {/* Select/unselect all buttons */}
                      <SelectOrUnselectAll
                        handler={selectOrUnselectAllCreateTags}
                        language={language}
                      />
                      <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                        {roomTags.map((tag: any) => (
                          <Checkbox
                            key={tag.id}
                            id={tag.id}
                            onChange={updateTagForCreateEvent}
                            checked={createEventForm.tags.includes(tag.id)}
                            label={tag.label}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Schedule */}
              <Dropdown
                id="schedule"
                className="col-span-full"
                label={translateText("schedule", language)}
                small={translateText("event_schedule_subtext", language)}
                showLabel={true}
                required={true}
                state={createEventForm}
                stateName="schedule"
                onChange={setCreateEventForm}
                defaultValue="O"
              >
                {eventScheduleChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* One off schedule builder */}
              {createEventForm.schedule === "O" && (
                <>
                  {/* Start datetime */}
                  <DatetimeSelector
                    label={translateText("start_datetime", language)}
                    showLabel={true}
                    dateState={oneOffScheduleStartDate}
                    dateHandler={setOneOffScheduleStartDate}
                    timeState={oneOffScheduleStartTime}
                    timeHandler={setOneOffScheduleStartTime}
                    language={language}
                  />
                  {/* End datetime */}
                  <DatetimeSelector
                    label={translateText("end_datetime", language)}
                    showLabel={true}
                    dateState={oneOffScheduleEndDate}
                    dateHandler={setOneOffScheduleEndDate}
                    timeState={oneOffScheduleEndTime}
                    timeHandler={setOneOffScheduleEndTime}
                    language={language}
                  />
                </>
              )}

              {/* Recurring schedule builder */}
              {createEventForm.schedule === "R" && (
                <>
                  {/* Schedule */}
                  <Dropdown
                    id="recurring_schedule_type"
                    className="col-span-full"
                    label={translateText("recurring_schedule_type", language)}
                    small={translateText(
                      "recurring_schedule_type_event_subtext",
                      language,
                    )}
                    showLabel={true}
                    required={true}
                    state={createEventForm}
                    stateName="recurring_schedule_type"
                    onChange={setCreateEventForm}
                    defaultValue="STD"
                  >
                    {eventScheduleRecurringChoices.map((opt) => (
                      <option key={opt[0]} value={opt[0]}>
                        {opt[1]}
                      </option>
                    ))}
                  </Dropdown>

                  {/* If recurring schedule is STD */}
                  {createEventForm.recurring_schedule_type === "STD" && (
                    <>
                      {/* Start time */}
                      <TimeSelector
                        label={translateText("start_time", language)}
                        showLabel={true}
                        timeState={recurringScheduleStartTime}
                        timeHandler={setRecurringScheduleStartTime}
                        language={language}
                      />
                      {/* End time */}
                      <TimeSelector
                        label={translateText("end_time", language)}
                        showLabel={true}
                        timeState={recurringScheduleEndTime}
                        timeHandler={setRecurringScheduleEndTime}
                        language={language}
                      />
                    </>
                  )}

                  {/* If recurring schedule is STW */}
                  {createEventForm.recurring_schedule_type === "STW" && (
                    <>
                      {createEventForm.recurring_schedule_detail.STW.map(
                        (value, index) => (
                          <div
                            key={index}
                            className="grid grid-cols-1 gap-y-8 border-t border-light pt-4"
                          >
                            {/* Day of week */}
                            <div>
                              {/* Delete button if necessary */}
                              {createEventForm.recurring_schedule_detail.STW
                                .length > 1 && (
                                <div className="flex justify-start">
                                  <Button
                                    type="button"
                                    className={`${getButtonStyle(
                                      "solid",
                                      "danger",
                                    )} mb-3`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      const existingValues =
                                        createEventForm
                                          .recurring_schedule_detail.STW;
                                      existingValues.splice(index, 1);
                                      const newValues = existingValues.map(
                                        (value, index) => ({
                                          ...value,
                                          id: index,
                                        }),
                                      );
                                      setCreateEventForm({
                                        ...createEventForm,
                                        recurring_schedule_detail: {
                                          ...createEventForm.recurring_schedule_detail,
                                          STW: newValues,
                                        },
                                      });
                                    }}
                                  >
                                    <TrashIcon
                                      className={`h-5 w-5 text-white`}
                                      aria-hidden="true"
                                    />
                                  </Button>
                                </div>
                              )}

                              <div className="col-span-full">
                                <select
                                  id={`dayofweek-${index}-recurring`}
                                  className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                  required={true}
                                  onChange={(e) => {
                                    const existingValues = [
                                      ...createEventForm
                                        .recurring_schedule_detail.STW,
                                    ];
                                    existingValues[index].value =
                                      e.target.value;
                                    setCreateEventForm({
                                      ...createEventForm,
                                      recurring_schedule_detail: {
                                        ...createEventForm.recurring_schedule_detail,
                                        STW: existingValues,
                                      },
                                    });
                                  }}
                                  value={parseInt(
                                    createEventForm.recurring_schedule_detail
                                      .STW[index].value,
                                    10,
                                  )}
                                >
                                  {daysOfTheWeek.map((opt) => (
                                    <option key={opt[0]} value={opt[0]}>
                                      {opt[1]}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            {/* Start and end time */}
                            <div className="grid md:grid-cols-2 grid-cols-1 gap-x-2 gap-y-6">
                              {/* Start time */}
                              <div>
                                <label
                                  className={`mb-3 block text-left text-sm font-medium text-black`}
                                >
                                  {translateText("start_time", language)}
                                </label>
                                <div className="grid grid-cols-2 gap-x-2">
                                  <select
                                    id={`dayofweek-start-time-hour-${index}-recurring`}
                                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                    required={true}
                                    onChange={(e) => {
                                      const existingValues = [
                                        ...createEventForm
                                          .recurring_schedule_detail.STW,
                                      ];
                                      existingValues[index].start_time.hour =
                                        e.target.value;
                                      setCreateEventForm({
                                        ...createEventForm,
                                        recurring_schedule_detail: {
                                          ...createEventForm.recurring_schedule_detail,
                                          STW: existingValues,
                                        },
                                      });
                                    }}
                                    defaultValue=""
                                  >
                                    <option value="">
                                      {translateText("hour", language)}
                                    </option>
                                    {hours.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt < 10 ? `0${opt}` : opt}
                                      </option>
                                    ))}
                                  </select>
                                  <select
                                    id={`dayofweek-start-time-minute-${index}-recurring`}
                                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                    required={true}
                                    onChange={(e) => {
                                      const existingValues = [
                                        ...createEventForm
                                          .recurring_schedule_detail.STW,
                                      ];
                                      existingValues[index].start_time.minute =
                                        e.target.value;
                                      setCreateEventForm({
                                        ...createEventForm,
                                        recurring_schedule_detail: {
                                          ...createEventForm.recurring_schedule_detail,
                                          STW: existingValues,
                                        },
                                      });
                                    }}
                                    defaultValue=""
                                  >
                                    <option value="">
                                      {translateText("minute", language)}
                                    </option>
                                    {minutes.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt < 10 ? `0${opt}` : opt}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>

                              {/* End time */}
                              <div>
                                <label
                                  className={`mb-3 block text-left text-sm font-medium text-black`}
                                >
                                  {translateText("end_time", language)}
                                </label>
                                <div className="grid grid-cols-2 gap-x-2">
                                  <select
                                    id={`dayofweek-end-time-hour-${index}-recurring`}
                                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                    required={true}
                                    onChange={(e) => {
                                      const existingValues = [
                                        ...createEventForm
                                          .recurring_schedule_detail.STW,
                                      ];
                                      existingValues[index].end_time.hour =
                                        e.target.value;
                                      setCreateEventForm({
                                        ...createEventForm,
                                        recurring_schedule_detail: {
                                          ...createEventForm.recurring_schedule_detail,
                                          STW: existingValues,
                                        },
                                      });
                                    }}
                                    defaultValue=""
                                  >
                                    <option value="">
                                      {translateText("hour", language)}
                                    </option>
                                    {hours.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt < 10 ? `0${opt}` : opt}
                                      </option>
                                    ))}
                                  </select>
                                  <select
                                    id={`dayofweek-end-time-minute-${index}-recurring`}
                                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                    required={true}
                                    onChange={(e) => {
                                      const existingValues = [
                                        ...createEventForm
                                          .recurring_schedule_detail.STW,
                                      ];
                                      existingValues[index].end_time.minute =
                                        e.target.value;
                                      setCreateEventForm({
                                        ...createEventForm,
                                        recurring_schedule_detail: {
                                          ...createEventForm.recurring_schedule_detail,
                                          STW: existingValues,
                                        },
                                      });
                                    }}
                                    defaultValue=""
                                  >
                                    <option value="">
                                      {translateText("minute", language)}
                                    </option>
                                    {minutes.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt < 10 ? `0${opt}` : opt}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            </div>
                          </div>
                        ),
                      )}

                      <Button
                        type="button"
                        className={`${getButtonStyle("solid", "primary")}`}
                        onClick={increaseRecurringDetailValueCount}
                        onClickArgs={["STW"]}
                      >
                        <PlusCircleIcon
                          className={`h-5 w-5 text-white mr-1`}
                          aria-hidden="true"
                        />
                        {translateText("add_another_time", language)}
                      </Button>
                    </>
                  )}

                  {/* If recurring schedule is STM */}
                  {createEventForm.recurring_schedule_type === "STM" && (
                    <>
                      {createEventForm.recurring_schedule_detail.STM.map(
                        (value, index) => (
                          <div
                            key={index}
                            className="grid grid-cols-1 gap-y-8 border-t border-light pt-4"
                          >
                            {/* Day of month */}
                            <div>
                              {/* Delete button if necessary */}
                              {createEventForm.recurring_schedule_detail.STM
                                .length > 1 && (
                                <div className="flex justify-start">
                                  <Button
                                    type="button"
                                    className={`${getButtonStyle(
                                      "solid",
                                      "danger",
                                    )} mb-3`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      const existingValues =
                                        createEventForm
                                          .recurring_schedule_detail.STM;
                                      existingValues.splice(index, 1);
                                      const newValues = existingValues.map(
                                        (value, index) => ({
                                          ...value,
                                          id: index,
                                        }),
                                      );
                                      setCreateEventForm({
                                        ...createEventForm,
                                        recurring_schedule_detail: {
                                          ...createEventForm.recurring_schedule_detail,
                                          STM: newValues,
                                        },
                                      });
                                    }}
                                  >
                                    <TrashIcon
                                      className={`h-5 w-5 text-white`}
                                      aria-hidden="true"
                                    />
                                  </Button>
                                </div>
                              )}

                              <div className="col-span-full">
                                <select
                                  id={`dayofmonth-${index}-recurring`}
                                  className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                  required={true}
                                  onChange={(e) => {
                                    const existingValues = [
                                      ...createEventForm
                                        .recurring_schedule_detail.STM,
                                    ];
                                    existingValues[index].value =
                                      e.target.value;
                                    setCreateEventForm({
                                      ...createEventForm,
                                      recurring_schedule_detail: {
                                        ...createEventForm.recurring_schedule_detail,
                                        STM: existingValues,
                                      },
                                    });
                                  }}
                                  value={parseInt(
                                    createEventForm.recurring_schedule_detail
                                      .STM[index].value,
                                    10,
                                  )}
                                >
                                  {daysOfTheMonth.map((opt) => (
                                    <option key={opt[0]} value={opt[0]}>
                                      {opt[1]}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            {/* Start and end time */}
                            <div className="grid md:grid-cols-2 grid-cols-1 gap-x-2 gap-y-6">
                              {/* Start time */}
                              <div>
                                <label
                                  className={`mb-3 block text-left text-sm font-medium text-black`}
                                >
                                  {translateText("start_time", language)}
                                </label>
                                <div className="grid grid-cols-2 gap-x-2">
                                  <select
                                    id={`dayofmonth-start-time-hour-${index}-recurring`}
                                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                    required={true}
                                    onChange={(e) => {
                                      const existingValues = [
                                        ...createEventForm
                                          .recurring_schedule_detail.STM,
                                      ];
                                      existingValues[index].start_time.hour =
                                        e.target.value;
                                      setCreateEventForm({
                                        ...createEventForm,
                                        recurring_schedule_detail: {
                                          ...createEventForm.recurring_schedule_detail,
                                          STM: existingValues,
                                        },
                                      });
                                    }}
                                    defaultValue=""
                                  >
                                    <option value="">
                                      {translateText("hour", language)}
                                    </option>
                                    {hours.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt < 10 ? `0${opt}` : opt}
                                      </option>
                                    ))}
                                  </select>
                                  <select
                                    id={`dayofmonth-start-time-minute-${index}-recurring`}
                                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                    required={true}
                                    onChange={(e) => {
                                      const existingValues = [
                                        ...createEventForm
                                          .recurring_schedule_detail.STM,
                                      ];
                                      existingValues[index].start_time.minute =
                                        e.target.value;
                                      setCreateEventForm({
                                        ...createEventForm,
                                        recurring_schedule_detail: {
                                          ...createEventForm.recurring_schedule_detail,
                                          STM: existingValues,
                                        },
                                      });
                                    }}
                                    defaultValue=""
                                  >
                                    <option value="">
                                      {translateText("minute", language)}
                                    </option>
                                    {minutes.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt < 10 ? `0${opt}` : opt}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>

                              {/* End time */}
                              <div>
                                <label
                                  className={`mb-3 block text-left text-sm font-medium text-black`}
                                >
                                  {translateText("end_time", language)}
                                </label>
                                <div className="grid grid-cols-2 gap-x-2">
                                  <select
                                    id={`dayofmonth-end-time-hour-${index}-recurring`}
                                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                    required={true}
                                    onChange={(e) => {
                                      const existingValues = [
                                        ...createEventForm
                                          .recurring_schedule_detail.STM,
                                      ];
                                      existingValues[index].end_time.hour =
                                        e.target.value;
                                      setCreateEventForm({
                                        ...createEventForm,
                                        recurring_schedule_detail: {
                                          ...createEventForm.recurring_schedule_detail,
                                          STM: existingValues,
                                        },
                                      });
                                    }}
                                    defaultValue=""
                                  >
                                    <option value="">
                                      {translateText("hour", language)}
                                    </option>
                                    {hours.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt < 10 ? `0${opt}` : opt}
                                      </option>
                                    ))}
                                  </select>
                                  <select
                                    id={`dayofweek-end-time-minute-${index}-recurring`}
                                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                    required={true}
                                    onChange={(e) => {
                                      const existingValues = [
                                        ...createEventForm
                                          .recurring_schedule_detail.STM,
                                      ];
                                      existingValues[index].end_time.minute =
                                        e.target.value;
                                      setCreateEventForm({
                                        ...createEventForm,
                                        recurring_schedule_detail: {
                                          ...createEventForm.recurring_schedule_detail,
                                          STM: existingValues,
                                        },
                                      });
                                    }}
                                    defaultValue=""
                                  >
                                    <option value="">
                                      {translateText("minute", language)}
                                    </option>
                                    {minutes.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt < 10 ? `0${opt}` : opt}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            </div>
                          </div>
                        ),
                      )}

                      <Button
                        type="button"
                        className={`${getButtonStyle("solid", "primary")}`}
                        onClick={increaseRecurringDetailValueCount}
                        onClickArgs={["STM"]}
                      >
                        <PlusCircleIcon
                          className={`h-5 w-5 text-white mr-1`}
                          aria-hidden="true"
                        />
                        {translateText("add_another_time", language)}
                      </Button>
                    </>
                  )}

                  {/* If recurring schedule is SLD */}
                  {createEventForm.recurring_schedule_type === "SLD" && (
                    <>
                      <Datepicker
                        i18n={getDatepickerLocalisation(language)}
                        asSingle={true}
                        startFrom={new Date()}
                        minDate={new Date()}
                        value={recurringSldPlaceholder}
                        placeholder={translateText("select_dates", language)}
                        displayFormat={""}
                        onChange={addDateToRecurringSld}
                      />
                      {createEventForm.recurring_schedule_detail.SLD.map(
                        (value, index) => (
                          <div
                            key={index}
                            className="grid grid-cols-1 gap-y-8 border-t border-light pt-4"
                          >
                            {/* Date */}
                            <div>
                              {/* Delete button if necessary */}
                              {createEventForm.recurring_schedule_detail.SLD
                                .length > 1 && (
                                <div className="flex justify-start">
                                  <Button
                                    type="button"
                                    className={`${getButtonStyle(
                                      "solid",
                                      "danger",
                                    )} mb-3`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      const existingValues =
                                        createEventForm
                                          .recurring_schedule_detail.SLD;
                                      existingValues.splice(index, 1);
                                      const newValues = existingValues.map(
                                        (value, index) => ({
                                          ...value,
                                          id: index,
                                        }),
                                      );
                                      setCreateEventForm({
                                        ...createEventForm,
                                        recurring_schedule_detail: {
                                          ...createEventForm.recurring_schedule_detail,
                                          SLD: newValues,
                                        },
                                      });
                                    }}
                                  >
                                    <TrashIcon
                                      className={`h-5 w-5 text-white`}
                                      aria-hidden="true"
                                    />
                                  </Button>
                                </div>
                              )}
                              <DisabledTextInput
                                id={`sld-date-${index}-recurring`}
                                label=""
                                className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                showLabel={false}
                                value={value.value}
                              />
                            </div>

                            {/* Start and end time */}
                            <div className="grid md:grid-cols-2 grid-cols-1 gap-x-2 gap-y-6">
                              {/* Start time */}
                              <div>
                                <label
                                  className={`mb-3 block text-left text-sm font-medium text-black`}
                                >
                                  {translateText("start_time", language)}
                                </label>
                                <div className="grid grid-cols-2 gap-x-2">
                                  <select
                                    id={`sld-start-time-hour-${index}-recurring`}
                                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                    required={true}
                                    onChange={(e) => {
                                      const existingValues = [
                                        ...createEventForm
                                          .recurring_schedule_detail.SLD,
                                      ];
                                      existingValues[index].start_time.hour =
                                        e.target.value;
                                      setCreateEventForm({
                                        ...createEventForm,
                                        recurring_schedule_detail: {
                                          ...createEventForm.recurring_schedule_detail,
                                          SLD: existingValues,
                                        },
                                      });
                                    }}
                                    defaultValue=""
                                  >
                                    <option value="">
                                      {translateText("hour", language)}
                                    </option>
                                    {hours.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt < 10 ? `0${opt}` : opt}
                                      </option>
                                    ))}
                                  </select>
                                  <select
                                    id={`sld-start-time-minute-${index}-recurring`}
                                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                    required={true}
                                    onChange={(e) => {
                                      const existingValues = [
                                        ...createEventForm
                                          .recurring_schedule_detail.SLD,
                                      ];
                                      existingValues[index].start_time.minute =
                                        e.target.value;
                                      setCreateEventForm({
                                        ...createEventForm,
                                        recurring_schedule_detail: {
                                          ...createEventForm.recurring_schedule_detail,
                                          SLD: existingValues,
                                        },
                                      });
                                    }}
                                    defaultValue=""
                                  >
                                    <option value="">
                                      {translateText("minute", language)}
                                    </option>
                                    {minutes.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt < 10 ? `0${opt}` : opt}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>

                              {/* End time */}
                              <div>
                                <label
                                  className={`mb-3 block text-left text-sm font-medium text-black`}
                                >
                                  {translateText("end_time", language)}
                                </label>
                                <div className="grid grid-cols-2 gap-x-2">
                                  <select
                                    id={`sld-end-time-hour-${index}-recurring`}
                                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                    required={true}
                                    onChange={(e) => {
                                      const existingValues = [
                                        ...createEventForm
                                          .recurring_schedule_detail.SLD,
                                      ];
                                      existingValues[index].end_time.hour =
                                        e.target.value;
                                      setCreateEventForm({
                                        ...createEventForm,
                                        recurring_schedule_detail: {
                                          ...createEventForm.recurring_schedule_detail,
                                          SLD: existingValues,
                                        },
                                      });
                                    }}
                                    defaultValue=""
                                  >
                                    <option value="">
                                      {translateText("hour", language)}
                                    </option>
                                    {hours.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt < 10 ? `0${opt}` : opt}
                                      </option>
                                    ))}
                                  </select>
                                  <select
                                    id={`sld-end-time-minute-${index}-recurring`}
                                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                    required={true}
                                    onChange={(e) => {
                                      const existingValues = [
                                        ...createEventForm
                                          .recurring_schedule_detail.SLD,
                                      ];
                                      existingValues[index].end_time.minute =
                                        e.target.value;
                                      setCreateEventForm({
                                        ...createEventForm,
                                        recurring_schedule_detail: {
                                          ...createEventForm.recurring_schedule_detail,
                                          SLD: existingValues,
                                        },
                                      });
                                    }}
                                    defaultValue=""
                                  >
                                    <option value="">
                                      {translateText("minute", language)}
                                    </option>
                                    {minutes.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt < 10 ? `0${opt}` : opt}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            </div>
                          </div>
                        ),
                      )}
                    </>
                  )}
                </>
              )}

              {/* URL */}
              <TextInput
                id="link"
                type="text"
                label={translateText("event_link", language)}
                className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={createEventForm}
                stateName="link"
                onChange={setCreateEventForm}
              />

              {/* Event background image */}
              <div>
                <label
                  htmlFor="payment_item_image"
                  className={`mb-3 block text-left text-sm font-medium text-black`}
                >
                  {translateText("background_image", language)}
                </label>
                <UploadFileDropzone
                  id="event_image"
                  className=""
                  limit={1}
                  page="events"
                  savedImages={[]}
                  blockId={null}
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit event modal */}
      <Modal
        show={showEditModal}
        setShow={setShowEditModal}
        confirmText={translateText("edit_event", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmEditEvent}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("edit_event", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Name */}
              <TextInputMultiLanguage
                id="name"
                type="text"
                label={translateText("name", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={selectedEvent}
                stateName="name"
                onChange={setSelectedEvent}
                defaultLanguage={language}
                languageChoices={
                  eventsData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(eventsData[0].name)
                }
              />

              {/* Event type */}
              <Dropdown
                id="type"
                className="col-span-full"
                label={translateText("type", language)}
                showLabel={true}
                required={true}
                state={selectedEvent}
                stateName="type"
                onChange={setSelectedEvent}
                defaultValue={selectedEvent.type}
              >
                {eventTypeChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Target */}
              <HorizontalRadio
                label={translateText("target_rooms", language)}
                small={translateText("target_rooms_subtext", language)}
              >
                <>
                  {targetTypes.map((choice) => (
                    <div key={choice[0]} className="flex items-center">
                      <input
                        id={choice[0]}
                        name="build_type"
                        type="radio"
                        defaultChecked={selectedEvent.target === choice[0]}
                        className="h-4 w-4 border-gray-300 text-primary focus:ring-primary rounded"
                        value={choice[0]}
                        onChange={(e) =>
                          setSelectedEvent({
                            ...selectedEvent,
                            target: e.target.value,
                          })
                        }
                      />
                      <label
                        htmlFor={choice[0]}
                        className="ml-3 block text-sm font-medium leading-6 text-black"
                      >
                        {choice[1]}
                      </label>
                    </div>
                  ))}
                </>
              </HorizontalRadio>

              {selectedEvent.target === "S" && (
                <>
                  {/* Choose rooms by */}
                  <Dropdown
                    id="choose_rooms_by"
                    className="col-span-full"
                    label={translateText("choose_rooms_by", language)}
                    small={translateText("choose_rooms_by_subtext", language)}
                    showLabel={true}
                    required={true}
                    state={selectedEvent}
                    stateName="choose_rooms_by"
                    onChange={setSelectedEvent}
                    defaultValue="name"
                  >
                    {chooseRoomsBy.map((opt) => (
                      <option key={opt[0]} value={opt[0]}>
                        {opt[1]}
                      </option>
                    ))}
                  </Dropdown>

                  {/* Rooms */}
                  {selectedEvent.choose_rooms_by === "name" ? (
                    <>
                      <div>
                        <label className="mb-1 block text-left text-sm font-medium text-black">
                          {translateText("rooms", language)}
                        </label>
                        <p className="text-left text-xs italic text-secondary">
                          {translateText("rooms_template_subtext", language)}
                        </p>
                      </div>

                      {/* Select/unselect all buttons */}
                      <SelectOrUnselectAll
                        handler={selectOrUnselectAllEditRooms}
                        language={language}
                      />
                      <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                        {roomsData.map((room: any) => (
                          <Checkbox
                            key={room.id}
                            id={room.id}
                            onChange={updateRoomForEditEvent}
                            checked={selectedEvent.rooms.includes(room.id)}
                            label={room.name}
                          />
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="mb-1 block text-left text-sm font-medium text-black">
                          {translateText("tag", language)}
                        </label>
                        <p className="text-left text-xs italic text-secondary">
                          {translateText(
                            "select_tags_for_rooms_campaign",
                            language,
                          )}
                        </p>
                      </div>

                      {/* Select/unselect all buttons */}
                      <SelectOrUnselectAll
                        handler={selectOrUnselectAllEditTags}
                        language={language}
                      />
                      <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                        {roomTags.map((tag: any) => (
                          <Checkbox
                            key={tag.id}
                            id={tag.id}
                            onChange={updateTagForEditEvent}
                            checked={selectedEvent.tags.includes(tag.id)}
                            label={tag.label}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Schedule */}
              <Dropdown
                id="schedule"
                className="col-span-full"
                label={translateText("schedule", language)}
                small={translateText("event_schedule_subtext", language)}
                showLabel={true}
                required={true}
                state={selectedEvent}
                stateName="schedule"
                onChange={setSelectedEvent}
                defaultValue={selectedEvent.schedule}
              >
                {eventScheduleChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* One off schedule builder */}
              {selectedEvent.schedule === "O" && (
                <>
                  {/* Start datetime */}
                  <DatetimeSelector
                    label={translateText("start_datetime", language)}
                    showLabel={true}
                    dateState={oneOffScheduleStartEditDate}
                    dateHandler={setOneOffScheduleStartEditDate}
                    timeState={oneOffScheduleStartEditTime}
                    timeHandler={setOneOffScheduleStartEditTime}
                    language={language}
                  />
                  {/* End datetime */}
                  <DatetimeSelector
                    label={translateText("end_datetime", language)}
                    showLabel={true}
                    dateState={oneOffScheduleEndEditDate}
                    dateHandler={setOneOffScheduleEndEditDate}
                    timeState={oneOffScheduleEndEditTime}
                    timeHandler={setOneOffScheduleEndEditTime}
                    language={language}
                  />
                </>
              )}

              {/* Recurring schedule builder */}
              {selectedEvent.schedule === "R" && (
                <>
                  {/* Schedule */}
                  <Dropdown
                    id="recurring_schedule_type"
                    className="col-span-full"
                    label={translateText("recurring_schedule_type", language)}
                    small={translateText(
                      "recurring_schedule_type_event_subtext",
                      language,
                    )}
                    showLabel={true}
                    required={true}
                    state={selectedEvent}
                    stateName="recurring_schedule_type"
                    onChange={setSelectedEvent}
                    defaultValue={
                      selectedEvent.recurring_schedule_type === null
                        ? "STD"
                        : selectedEvent.recurring_schedule_type
                    }
                  >
                    {eventScheduleRecurringChoices.map((opt) => (
                      <option key={opt[0]} value={opt[0]}>
                        {opt[1]}
                      </option>
                    ))}
                  </Dropdown>

                  {/* If recurring schedule is STD */}
                  {selectedEvent.recurring_schedule_type === "STD" && (
                    <>
                      {/* Start time */}
                      <TimeSelector
                        label={translateText("start_time", language)}
                        showLabel={true}
                        timeState={recurringScheduleStartEditTime}
                        timeHandler={setRecurringScheduleStartEditTime}
                        language={language}
                      />
                      {/* End time */}
                      <TimeSelector
                        label={translateText("end_time", language)}
                        showLabel={true}
                        timeState={recurringScheduleEndEditTime}
                        timeHandler={setRecurringScheduleEndEditTime}
                        language={language}
                      />
                    </>
                  )}

                  {/* If recurring schedule is STW */}
                  {selectedEvent.recurring_schedule_type === "STW" && (
                    <>
                      {selectedEvent.recurring_schedule_detail.STW.map(
                        (value, index) => (
                          <div
                            key={index}
                            className="grid grid-cols-1 gap-y-8 border-t border-light pt-4"
                          >
                            {/* Day of week */}
                            <div>
                              {/* Delete button if necessary */}
                              {selectedEvent.recurring_schedule_detail.STW
                                .length > 1 && (
                                <div className="flex justify-start">
                                  <Button
                                    type="button"
                                    className={`${getButtonStyle(
                                      "solid",
                                      "danger",
                                    )} mb-3`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      const existingValues =
                                        selectedEvent.recurring_schedule_detail
                                          .STW;
                                      existingValues.splice(index, 1);
                                      const newValues = existingValues.map(
                                        (value, index) => ({
                                          ...value,
                                          id: index,
                                        }),
                                      );
                                      setSelectedEvent({
                                        ...selectedEvent,
                                        recurring_schedule_detail: {
                                          ...selectedEvent.recurring_schedule_detail,
                                          STW: newValues,
                                        },
                                      });
                                    }}
                                  >
                                    <TrashIcon
                                      className={`h-5 w-5 text-white`}
                                      aria-hidden="true"
                                    />
                                  </Button>
                                </div>
                              )}

                              <div className="col-span-full">
                                <select
                                  id={`dayofweek-${index}-recurring`}
                                  className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                  required={true}
                                  onChange={(e) => {
                                    const existingValues = [
                                      ...selectedEvent.recurring_schedule_detail
                                        .STW,
                                    ];
                                    existingValues[index].value =
                                      e.target.value;
                                    setSelectedEvent({
                                      ...selectedEvent,
                                      recurring_schedule_detail: {
                                        ...selectedEvent.recurring_schedule_detail,
                                        STW: existingValues,
                                      },
                                    });
                                  }}
                                  value={parseInt(
                                    selectedEvent.recurring_schedule_detail.STW[
                                      index
                                    ].value,
                                    10,
                                  )}
                                >
                                  {daysOfTheWeek.map((opt) => (
                                    <option key={opt[0]} value={opt[0]}>
                                      {opt[1]}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            {/* Start and end time */}
                            <div className="grid md:grid-cols-2 grid-cols-1 gap-x-2 gap-y-6">
                              {/* Start time */}
                              <div>
                                <label
                                  className={`mb-3 block text-left text-sm font-medium text-black`}
                                >
                                  {translateText("start_time", language)}
                                </label>
                                <div className="grid grid-cols-2 gap-x-2">
                                  <select
                                    id={`dayofweek-start-time-hour-${index}-recurring`}
                                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                    required={true}
                                    onChange={(e) => {
                                      const existingValues = [
                                        ...selectedEvent
                                          .recurring_schedule_detail.STW,
                                      ];
                                      existingValues[index].start_time.hour =
                                        e.target.value;
                                      setSelectedEvent({
                                        ...selectedEvent,
                                        recurring_schedule_detail: {
                                          ...selectedEvent.recurring_schedule_detail,
                                          STW: existingValues,
                                        },
                                      });
                                    }}
                                    defaultValue={selectedEvent.recurring_schedule_detail.STW[
                                      index
                                    ].start_time.hour.toString()}
                                  >
                                    <option value="">
                                      {translateText("hour", language)}
                                    </option>
                                    {hours.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt < 10 ? `0${opt}` : opt}
                                      </option>
                                    ))}
                                  </select>
                                  <select
                                    id={`dayofweek-start-time-minute-${index}-recurring`}
                                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                    required={true}
                                    onChange={(e) => {
                                      const existingValues = [
                                        ...selectedEvent
                                          .recurring_schedule_detail.STW,
                                      ];
                                      existingValues[index].start_time.minute =
                                        e.target.value;
                                      setSelectedEvent({
                                        ...selectedEvent,
                                        recurring_schedule_detail: {
                                          ...selectedEvent.recurring_schedule_detail,
                                          STW: existingValues,
                                        },
                                      });
                                    }}
                                    defaultValue={selectedEvent.recurring_schedule_detail.STW[
                                      index
                                    ].start_time.minute.toString()}
                                  >
                                    <option value="">
                                      {translateText("minute", language)}
                                    </option>
                                    {minutes.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt < 10 ? `0${opt}` : opt}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>

                              {/* End time */}
                              <div>
                                <label
                                  className={`mb-3 block text-left text-sm font-medium text-black`}
                                >
                                  {translateText("end_time", language)}
                                </label>
                                <div className="grid grid-cols-2 gap-x-2">
                                  <select
                                    id={`dayofweek-end-time-hour-${index}-recurring`}
                                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                    required={true}
                                    onChange={(e) => {
                                      const existingValues = [
                                        ...selectedEvent
                                          .recurring_schedule_detail.STW,
                                      ];
                                      existingValues[index].end_time.hour =
                                        e.target.value;
                                      setSelectedEvent({
                                        ...selectedEvent,
                                        recurring_schedule_detail: {
                                          ...selectedEvent.recurring_schedule_detail,
                                          STW: existingValues,
                                        },
                                      });
                                    }}
                                    defaultValue={selectedEvent.recurring_schedule_detail.STW[
                                      index
                                    ].end_time.hour.toString()}
                                  >
                                    <option value="">
                                      {translateText("hour", language)}
                                    </option>
                                    {hours.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt < 10 ? `0${opt}` : opt}
                                      </option>
                                    ))}
                                  </select>
                                  <select
                                    id={`dayofweek-end-time-minute-${index}-recurring`}
                                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                    required={true}
                                    onChange={(e) => {
                                      const existingValues = [
                                        ...selectedEvent
                                          .recurring_schedule_detail.STW,
                                      ];
                                      existingValues[index].end_time.minute =
                                        e.target.value;
                                      setSelectedEvent({
                                        ...selectedEvent,
                                        recurring_schedule_detail: {
                                          ...selectedEvent.recurring_schedule_detail,
                                          STW: existingValues,
                                        },
                                      });
                                    }}
                                    defaultValue={selectedEvent.recurring_schedule_detail.STW[
                                      index
                                    ].end_time.minute.toString()}
                                  >
                                    <option value="">
                                      {translateText("minute", language)}
                                    </option>
                                    {minutes.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt < 10 ? `0${opt}` : opt}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            </div>
                          </div>
                        ),
                      )}

                      <Button
                        type="button"
                        className={`${getButtonStyle("solid", "primary")}`}
                        onClick={increaseRecurringDetailValueEditCount}
                        onClickArgs={["STW"]}
                      >
                        <PlusCircleIcon
                          className={`h-5 w-5 text-white mr-1`}
                          aria-hidden="true"
                        />
                        {translateText("add_another_time", language)}
                      </Button>
                    </>
                  )}

                  {/* If recurring schedule is STM */}
                  {selectedEvent.recurring_schedule_type === "STM" && (
                    <>
                      {selectedEvent.recurring_schedule_detail.STM.map(
                        (value, index) => (
                          <div
                            key={index}
                            className="grid grid-cols-1 gap-y-8 border-t border-light pt-4"
                          >
                            {/* Day of month */}
                            <div>
                              {/* Delete button if necessary */}
                              {selectedEvent.recurring_schedule_detail.STM
                                .length > 1 && (
                                <div className="flex justify-start">
                                  <Button
                                    type="button"
                                    className={`${getButtonStyle(
                                      "solid",
                                      "danger",
                                    )} mb-3`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      const existingValues =
                                        selectedEvent.recurring_schedule_detail
                                          .STM;
                                      existingValues.splice(index, 1);
                                      const newValues = existingValues.map(
                                        (value, index) => ({
                                          ...value,
                                          id: index,
                                        }),
                                      );
                                      setSelectedEvent({
                                        ...selectedEvent,
                                        recurring_schedule_detail: {
                                          ...selectedEvent.recurring_schedule_detail,
                                          STM: newValues,
                                        },
                                      });
                                    }}
                                  >
                                    <TrashIcon
                                      className={`h-5 w-5 text-white`}
                                      aria-hidden="true"
                                    />
                                  </Button>
                                </div>
                              )}

                              <div className="col-span-full">
                                <select
                                  id={`dayofmonth-${index}-recurring`}
                                  className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                  required={true}
                                  onChange={(e) => {
                                    const existingValues = [
                                      ...selectedEvent.recurring_schedule_detail
                                        .STM,
                                    ];
                                    existingValues[index].value =
                                      e.target.value;
                                    setSelectedEvent({
                                      ...selectedEvent,
                                      recurring_schedule_detail: {
                                        ...selectedEvent.recurring_schedule_detail,
                                        STM: existingValues,
                                      },
                                    });
                                  }}
                                  value={parseInt(
                                    selectedEvent.recurring_schedule_detail.STM[
                                      index
                                    ].value,
                                    10,
                                  )}
                                >
                                  {daysOfTheMonth.map((opt) => (
                                    <option key={opt[0]} value={opt[0]}>
                                      {opt[1]}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            {/* Start and end time */}
                            <div className="grid md:grid-cols-2 grid-cols-1 gap-x-2 gap-y-6">
                              {/* Start time */}
                              <div>
                                <label
                                  className={`mb-3 block text-left text-sm font-medium text-black`}
                                >
                                  {translateText("start_time", language)}
                                </label>
                                <div className="grid grid-cols-2 gap-x-2">
                                  <select
                                    id={`dayofmonth-start-time-hour-${index}-recurring`}
                                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                    required={true}
                                    onChange={(e) => {
                                      const existingValues = [
                                        ...selectedEvent
                                          .recurring_schedule_detail.STM,
                                      ];
                                      existingValues[index].start_time.hour =
                                        e.target.value;
                                      setSelectedEvent({
                                        ...selectedEvent,
                                        recurring_schedule_detail: {
                                          ...selectedEvent.recurring_schedule_detail,
                                          STM: existingValues,
                                        },
                                      });
                                    }}
                                    defaultValue={selectedEvent.recurring_schedule_detail.STM[
                                      index
                                    ].start_time.hour.toString()}
                                  >
                                    <option value="">
                                      {translateText("hour", language)}
                                    </option>
                                    {hours.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt < 10 ? `0${opt}` : opt}
                                      </option>
                                    ))}
                                  </select>
                                  <select
                                    id={`dayofmonth-start-time-minute-${index}-recurring`}
                                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                    required={true}
                                    onChange={(e) => {
                                      const existingValues = [
                                        ...selectedEvent
                                          .recurring_schedule_detail.STM,
                                      ];
                                      existingValues[index].start_time.minute =
                                        e.target.value;
                                      setSelectedEvent({
                                        ...selectedEvent,
                                        recurring_schedule_detail: {
                                          ...selectedEvent.recurring_schedule_detail,
                                          STM: existingValues,
                                        },
                                      });
                                    }}
                                    defaultValue={selectedEvent.recurring_schedule_detail.STM[
                                      index
                                    ].start_time.minute.toString()}
                                  >
                                    <option value="">
                                      {translateText("minute", language)}
                                    </option>
                                    {minutes.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt < 10 ? `0${opt}` : opt}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>

                              {/* End time */}
                              <div>
                                <label
                                  className={`mb-3 block text-left text-sm font-medium text-black`}
                                >
                                  {translateText("end_time", language)}
                                </label>
                                <div className="grid grid-cols-2 gap-x-2">
                                  <select
                                    id={`dayofmonth-end-time-hour-${index}-recurring`}
                                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                    required={true}
                                    onChange={(e) => {
                                      const existingValues = [
                                        ...selectedEvent
                                          .recurring_schedule_detail.STM,
                                      ];
                                      existingValues[index].end_time.hour =
                                        e.target.value;
                                      setSelectedEvent({
                                        ...selectedEvent,
                                        recurring_schedule_detail: {
                                          ...selectedEvent.recurring_schedule_detail,
                                          STM: existingValues,
                                        },
                                      });
                                    }}
                                    defaultValue={selectedEvent.recurring_schedule_detail.STM[
                                      index
                                    ].end_time.hour.toString()}
                                  >
                                    <option value="">
                                      {translateText("hour", language)}
                                    </option>
                                    {hours.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt < 10 ? `0${opt}` : opt}
                                      </option>
                                    ))}
                                  </select>
                                  <select
                                    id={`dayofweek-end-time-minute-${index}-recurring`}
                                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                    required={true}
                                    onChange={(e) => {
                                      const existingValues = [
                                        ...selectedEvent
                                          .recurring_schedule_detail.STM,
                                      ];
                                      existingValues[index].end_time.minute =
                                        e.target.value;
                                      setSelectedEvent({
                                        ...selectedEvent,
                                        recurring_schedule_detail: {
                                          ...selectedEvent.recurring_schedule_detail,
                                          STM: existingValues,
                                        },
                                      });
                                    }}
                                    defaultValue={selectedEvent.recurring_schedule_detail.STM[
                                      index
                                    ].end_time.minute.toString()}
                                  >
                                    <option value="">
                                      {translateText("minute", language)}
                                    </option>
                                    {minutes.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt < 10 ? `0${opt}` : opt}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            </div>
                          </div>
                        ),
                      )}

                      <Button
                        type="button"
                        className={`${getButtonStyle("solid", "primary")}`}
                        onClick={increaseRecurringDetailValueEditCount}
                        onClickArgs={["STM"]}
                      >
                        <PlusCircleIcon
                          className={`h-5 w-5 text-white mr-1`}
                          aria-hidden="true"
                        />
                        {translateText("add_another_time", language)}
                      </Button>
                    </>
                  )}

                  {/* If recurring schedule is SLD */}
                  {selectedEvent.recurring_schedule_type === "SLD" && (
                    <>
                      <Datepicker
                        i18n={getDatepickerLocalisation(language)}
                        asSingle={true}
                        startFrom={new Date()}
                        minDate={new Date()}
                        value={recurringSldPlaceholder}
                        placeholder={translateText("select_dates", language)}
                        displayFormat={""}
                        onChange={addDateToRecurringEditSld}
                      />
                      {selectedEvent.recurring_schedule_detail.SLD.map(
                        (value, index) => (
                          <div
                            key={index}
                            className="grid grid-cols-1 gap-y-8 border-t border-light pt-4"
                          >
                            {/* Date */}
                            <div>
                              {/* Delete button if necessary */}
                              {selectedEvent.recurring_schedule_detail.SLD
                                .length > 1 && (
                                <div className="flex justify-start">
                                  <Button
                                    type="button"
                                    className={`${getButtonStyle(
                                      "solid",
                                      "danger",
                                    )} mb-3`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      const existingValues =
                                        selectedEvent.recurring_schedule_detail
                                          .SLD;
                                      existingValues.splice(index, 1);
                                      const newValues = existingValues.map(
                                        (value, index) => ({
                                          ...value,
                                          id: index,
                                        }),
                                      );
                                      setSelectedEvent({
                                        ...selectedEvent,
                                        recurring_schedule_detail: {
                                          ...selectedEvent.recurring_schedule_detail,
                                          SLD: newValues,
                                        },
                                      });
                                    }}
                                  >
                                    <TrashIcon
                                      className={`h-5 w-5 text-white`}
                                      aria-hidden="true"
                                    />
                                  </Button>
                                </div>
                              )}
                              <DisabledTextInput
                                id={`sld-date-${index}-recurring`}
                                label=""
                                className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                showLabel={false}
                                value={value.value}
                              />
                            </div>

                            {/* Start and end time */}
                            <div className="grid md:grid-cols-2 grid-cols-1 gap-x-2 gap-y-6">
                              {/* Start time */}
                              <div>
                                <label
                                  className={`mb-3 block text-left text-sm font-medium text-black`}
                                >
                                  {translateText("start_time", language)}
                                </label>
                                <div className="grid grid-cols-2 gap-x-2">
                                  <select
                                    id={`sld-start-time-hour-${index}-recurring`}
                                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                    required={true}
                                    onChange={(e) => {
                                      const existingValues = [
                                        ...selectedEvent
                                          .recurring_schedule_detail.SLD,
                                      ];
                                      existingValues[index].start_time.hour =
                                        e.target.value;
                                      setSelectedEvent({
                                        ...selectedEvent,
                                        recurring_schedule_detail: {
                                          ...selectedEvent.recurring_schedule_detail,
                                          SLD: existingValues,
                                        },
                                      });
                                    }}
                                    defaultValue={selectedEvent.recurring_schedule_detail.SLD[
                                      index
                                    ].start_time.hour.toString()}
                                  >
                                    <option value="">
                                      {translateText("hour", language)}
                                    </option>
                                    {hours.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt < 10 ? `0${opt}` : opt}
                                      </option>
                                    ))}
                                  </select>
                                  <select
                                    id={`sld-start-time-minute-${index}-recurring`}
                                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                    required={true}
                                    onChange={(e) => {
                                      const existingValues = [
                                        ...selectedEvent
                                          .recurring_schedule_detail.SLD,
                                      ];
                                      existingValues[index].start_time.minute =
                                        e.target.value;
                                      setSelectedEvent({
                                        ...selectedEvent,
                                        recurring_schedule_detail: {
                                          ...selectedEvent.recurring_schedule_detail,
                                          SLD: existingValues,
                                        },
                                      });
                                    }}
                                    defaultValue={selectedEvent.recurring_schedule_detail.SLD[
                                      index
                                    ].start_time.minute.toString()}
                                  >
                                    <option value="">
                                      {translateText("minute", language)}
                                    </option>
                                    {minutes.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt < 10 ? `0${opt}` : opt}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>

                              {/* End time */}
                              <div>
                                <label
                                  className={`mb-3 block text-left text-sm font-medium text-black`}
                                >
                                  {translateText("end_time", language)}
                                </label>
                                <div className="grid grid-cols-2 gap-x-2">
                                  <select
                                    id={`sld-end-time-hour-${index}-recurring`}
                                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                    required={true}
                                    onChange={(e) => {
                                      const existingValues = [
                                        ...selectedEvent
                                          .recurring_schedule_detail.SLD,
                                      ];
                                      existingValues[index].end_time.hour =
                                        e.target.value;
                                      setSelectedEvent({
                                        ...selectedEvent,
                                        recurring_schedule_detail: {
                                          ...selectedEvent.recurring_schedule_detail,
                                          SLD: existingValues,
                                        },
                                      });
                                    }}
                                    defaultValue={selectedEvent.recurring_schedule_detail.SLD[
                                      index
                                    ].end_time.hour.toString()}
                                  >
                                    <option value="">
                                      {translateText("hour", language)}
                                    </option>
                                    {hours.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt < 10 ? `0${opt}` : opt}
                                      </option>
                                    ))}
                                  </select>
                                  <select
                                    id={`sld-end-time-minute-${index}-recurring`}
                                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                    required={true}
                                    onChange={(e) => {
                                      const existingValues = [
                                        ...selectedEvent
                                          .recurring_schedule_detail.SLD,
                                      ];
                                      existingValues[index].end_time.minute =
                                        e.target.value;
                                      setSelectedEvent({
                                        ...selectedEvent,
                                        recurring_schedule_detail: {
                                          ...selectedEvent.recurring_schedule_detail,
                                          SLD: existingValues,
                                        },
                                      });
                                    }}
                                    defaultValue={selectedEvent.recurring_schedule_detail.SLD[
                                      index
                                    ].end_time.minute.toString()}
                                  >
                                    <option value="">
                                      {translateText("minute", language)}
                                    </option>
                                    {minutes.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {opt < 10 ? `0${opt}` : opt}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            </div>
                          </div>
                        ),
                      )}
                    </>
                  )}
                </>
              )}

              {/* URL */}
              <div>
                <TextInput
                  id="link"
                  type="text"
                  label={translateText("event_link", language)}
                  className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                  required={true}
                  showLabel={true}
                  state={selectedEvent}
                  stateName="link"
                  onChange={setSelectedEvent}
                />
                {(selectedEvent.link !== "" || selectedEvent.link !== null) &&
                  selectedEvent.qr_code !== null && (
                    <EyeIcon
                      className="mt-3 h-5 w-5 text-black hover:text-info"
                      onMouseEnter={() => setShowQrCode(true)}
                      onMouseLeave={() => setShowQrCode(false)}
                    />
                  )}
                {showQrCode && selectedEvent.qr_code !== null && (
                  <div>
                    <img
                      className="rounded-md border-0 h-40 mx-auto border-1 border-secondary drop-shadow"
                      src={selectedEvent.qr_code}
                      alt="event link qr code"
                      aria-hidden="true"
                      height={12}
                    />
                  </div>
                )}
              </div>

              {/* Event background image */}
              <div>
                <label
                  htmlFor="payment_item_image"
                  className={`mb-3 block text-left text-sm font-medium text-black`}
                >
                  {translateText("background_image", language)}
                </label>
                <UploadFileDropzone
                  id="event_edit_image"
                  className=""
                  limit={1}
                  page="events"
                  savedImages={getSavedImages()}
                  blockId={null}
                />
              </div>

              {/* Status */}
              <SwitchToggle
                id="status"
                label={translateText("status", language)}
                description={translateText("status_event_subtext", language)}
                showLabel={true}
                toggle={selectedEvent.status === "A"}
                state={selectedEvent}
                stateName="status"
                handler={setSelectedEvent}
                mapping={{ true: "A", false: "I" }}
              />

              {/* Danger zone */}
              <DangerZone
                objectId={selectedEvent.id}
                handler={confirmDeleteEvent}
                language={language}
              />
            </div>
          </div>
        </div>
      </Modal>

      {/* Loading spinner */}
      {events.loading || rooms.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            <Filters
              language={language}
              className="pb-10"
              filters={[
                {
                  id: "type",
                  name: "Type",
                  options: eventTypes,
                },
              ]}
              queryParams={[
                ["type", searchType],
                ["dateRange", dateRangeTerm],
              ]}
              handlers={{
                type: {
                  state: type,
                  stateName: "value",
                  onChange: setType,
                },
                dateRange: {
                  state: dateRange,
                  stateName: "N/A",
                  onChange: setDateRange,
                },
              }}
              loading={events.loading}
              baseUrl="/content/events"
              showDateRange={true}
              showFilters={true}
              showSearchBar={false}
            />
            {eventsData.length > 0 ? (
              <>
                <div className="mx-1 mb-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {eventsData.map((event: EventState) => (
                    <a
                      href="#!"
                      key={event.id}
                      onClick={(e) => selectEventToEdit(e, [event.id])}
                      className="relative"
                    >
                      {/* Event data */}
                      <Card
                        key={event.id}
                        colour="white"
                        className={`hover:ring-1 hover:ring-secondary/50 hover:ring-inset`}
                      >
                        <div className={`border-b border-light pb-2`}>
                          <div className="items-center justify-between">
                            <div>
                              <h3
                                className={`text-base font-semibold leading-6 text-black truncate`}
                              >
                                {event.name[language]}
                              </h3>
                            </div>
                          </div>
                        </div>
                        <div className="pt-2 grid grid-cols-1 space-y-2">
                          <div className="inline-flex text-sm text-secondary">
                            <Square3Stack3DIcon
                              className="h-5 w-5 text-info mr-1"
                              aria-hidden="true"
                            />
                            {event.type[1]}
                          </div>
                          <div className="inline-flex text-sm text-secondary">
                            {event.schedule[0] === "O" ? (
                              <ArrowLongRightIcon
                                className="h-5 w-5 text-secondary mr-1"
                                aria-hidden="true"
                              />
                            ) : (
                              <ArrowPathIcon
                                className="h-5 w-5 text-secondary mr-1"
                                aria-hidden="true"
                              />
                            )}
                            {event.schedule[1]}
                          </div>
                          <div className="inline-flex text-sm text-secondary">
                            <ClockIcon
                              className="h-5 w-5 text-secondary mr-1"
                              aria-hidden="true"
                            />
                            {event.next_occurrence.string}
                          </div>
                        </div>
                      </Card>

                      {/* Status indicator */}
                      <Indicator
                        size={3}
                        colour={event.status[0] === "A" ? "success" : "warning"}
                        pulse={false}
                      />
                    </a>
                  ))}
                </div>
              </>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("no_events", language)}
                subtext={translateText("no_events_subtext", language)}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
